import { useCallback, useRef } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import useNativeMessages from '@/hooks/native/useNativeMessages';
import useIsNative from '@/hooks/native/useIsNative';

const useKeyValueStorage = <T>(key: string, defaultValue: T) => {
	const [localStorageValue, setLocalStorageValue] = useLocalStorage<T>(
		`soortsafari-${key}`,
		defaultValue
	);

	const isNative = useIsNative();
	const lastSentValueToNativeAsJSON = useRef<string>('');

	const { sendNativeMessage } = useNativeMessages();

	const setValue = useCallback(
		(value: T | ((prev: T) => T)) => {
			// Use functional update to avoid stale closures and compute the next value
			setLocalStorageValue((prev: T) => {
				const next = typeof value === 'function' ? (value as (p: T) => T)(prev) : value;
				// If native and value changed, back up this value to native storage
				const nextJSON = JSON.stringify(next);
				if (isNative && nextJSON !== lastSentValueToNativeAsJSON.current) {
					sendNativeMessage('keyValue.set', `${key}=${nextJSON}`);
					lastSentValueToNativeAsJSON.current = nextJSON;
				}
				return next;
			});
		},
		[setLocalStorageValue, isNative, sendNativeMessage, key]
	);

	return {
		value: localStorageValue,
		setValue,
	};
};

export default useKeyValueStorage;
